import { apiRequest } from '@/utils/api-request';
import { NodeFile } from '@/types/node-types';

/**
 * Response from the refresh file URLs endpoint.
 */
interface RefreshedFileUrl {
  s3_key: string;
  presigned_url: string | null; // For viewing/preview
  download_url: string | null; // For download with correct filename
  proxy_url: string | null; // Short URL for Office Online preview
  error: string | null;
}

interface RefreshUrlsResponse {
  success: boolean;
  message: string;
  files: RefreshedFileUrl[];
  count: number;
}

/**
 * Refresh file URLs (presigned, download, and proxy) for multiple files using S3 metadata.
 *
 * Uses the batch endpoint that works with S3 bucket/key/region,
 * so it works for ALL files - including live execution files without database IDs.
 * The backend includes Content-Disposition header in the URL for proper filename downloads.
 *
 * @param orgId - Organization ID
 * @param files - Array of NodeFile objects with S3 metadata
 * @returns Promise containing array of NodeFile with fresh URLs
 */
export async function refreshFileUrls(
  orgId: string,
  files: NodeFile[],
): Promise<NodeFile[]> {
  // Build request payload with S3 metadata
  const requestFiles = files.map((f) => ({
    s3_bucket: f.s3_bucket,
    s3_key: f.s3_key,
    s3_region: f.s3_region,
    name: f.name,
    mime_type: f.mime_type,
  }));

  const response = await apiRequest<RefreshUrlsResponse>({
    url: `/api/v1/files/refresh-urls/${orgId}`,
    target: 'workflow',
    method: 'POST',
    body: { files: requestFiles },
  });

  // Handle both wrapped and unwrapped responses
  const data = (response.data || response) as RefreshUrlsResponse;

  if (!data?.success) {
    throw new Error(data?.message || 'Failed to refresh presigned URLs');
  }

  // Build lookup maps: s3_key -> URLs
  const urlMap = new Map<
    string,
    {
      presigned_url: string | null;
      download_url: string | null;
      proxy_url: string | null;
    }
  >();
  for (const refreshed of data.files) {
    urlMap.set(refreshed.s3_key, {
      presigned_url: refreshed.presigned_url,
      download_url: refreshed.download_url,
      proxy_url: refreshed.proxy_url,
    });
  }

  // Update files with fresh URLs, maintaining original order
  return files.map((file) => {
    const urls = urlMap.get(file.s3_key);
    if (urls?.presigned_url) {
      return {
        ...file,
        presigned_url: urls.presigned_url,
        download_url: urls.download_url || undefined,
        proxy_url: urls.proxy_url || undefined,
      };
    }
    // Keep original URL if refresh failed for this file
    return file;
  });
}

/**
 * Download a file using direct navigation.
 *
 * The presigned URL includes a Content-Disposition header (set by the backend)
 * that tells the browser to download the file with the correct filename.
 * This avoids CORS issues since we're not using fetch.
 *
 * @param presignedUrl - Presigned URL to download from (includes Content-Disposition)
 * @param filename - Filename for logging purposes (actual filename comes from URL header)
 */
export async function downloadFileWithName(
  presignedUrl: string,
  filename: string,
): Promise<void> {
  // Validate inputs
  if (!presignedUrl) {
    throw new Error('No presigned URL provided for download');
  }

  console.log(`[Download] Starting download: ${filename}`);

  // Use direct navigation - the presigned URL includes Content-Disposition header
  // which tells the browser to download with the correct filename
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = presignedUrl;
  link.target = '_blank'; // Open in new tab to handle download
  link.rel = 'noopener noreferrer';

  // Note: We don't set link.download here because:
  // 1. It doesn't work for cross-origin URLs
  // 2. The Content-Disposition header from S3 will set the filename

  // Append to body (required for Firefox)
  document.body.appendChild(link);

  // Trigger download
  link.click();

  console.log(`[Download] Download triggered for: ${filename}`);

  // Cleanup immediately - the browser handles the download
  setTimeout(() => {
    document.body.removeChild(link);
    console.log(`[Download] Cleanup complete for: ${filename}`);
  }, 100);
}
